import { useEffect, useRef } from 'react';

// Modal stack to track all open modals
// Using a Set to automatically handle duplicates and track unique modal instances
const modalStack = new Set();

// Store scroll position when first modal opens
let savedScrollPosition = 0;

/**
 * Gets the current scroll position from window or document
 * @returns {number} Current scroll position
 */
const getScrollPosition = () => {
  if (window.pageYOffset !== undefined) {
    return window.pageYOffset;
  }
  if (document.documentElement) {
    return document.documentElement.scrollTop;
  }
  if (document.body) {
    return document.body.scrollTop;
  }
  return 0;
};

/**
 * Scrolls the page to the top (used to prevent scroll when modal opens)
 */
const scrollToTop = () => {
  if (window.scrollTo) {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  } else {
    if (window.scroll) {
      window.scroll(0, 0);
    }
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
  }
};

/**
 * Updates the body tag with modal-open class based on modal stack
 * Only removes the class when no modals are open
 * Optimized to avoid unnecessary DOM manipulation
 * Also handles scroll position to prevent page jumping
 */
const updateModalOpenClass = () => {
  const hasModals = modalStack.size > 0;
  const bodyElement = document.body;
  const htmlElement = document.documentElement;
  
  if (!bodyElement) return;
  
  if (hasModals && !bodyElement.classList.contains('modal-open')) {
    // Save current scroll position before opening modal
    savedScrollPosition = getScrollPosition();
    
    // Add modal-open class
    bodyElement.classList.add('modal-open');
    
    // Prevent scroll by setting body position and top
    // The negative top value preserves the visual scroll position
    bodyElement.style.position = 'fixed';
    bodyElement.style.top = `-${savedScrollPosition}px`;
    bodyElement.style.width = '100%';
    
    // Also prevent scroll on html element
    if (htmlElement) {
      htmlElement.style.overflow = 'hidden';
    }
  } else if (!hasModals && bodyElement.classList.contains('modal-open')) {
    // Remove modal-open class
    bodyElement.classList.remove('modal-open');
    
    // Restore body styles
    // bodyElement.style.overflow = '';
    // bodyElement.style.position = '';
    // bodyElement.style.top = '';
    // bodyElement.style.width = '';
    
    // Restore html overflow
    if (htmlElement) {
      htmlElement.style.overflow = '';
    }
    
    // Restore scroll position
    if (window.scrollTo) {
      window.scrollTo({
        top: savedScrollPosition,
        left: 0,
        behavior: 'instant'
      });
    } else {
      if (document.documentElement) {
        document.documentElement.scrollTop = savedScrollPosition;
      }
      if (document.body) {
        document.body.scrollTop = savedScrollPosition;
      }
    }
    
    savedScrollPosition = 0;
  }
};

/**
 * Custom hook to manage the 'modal-open' class on the body element.
 * This prevents body scrolling when a modal is open.
 * Uses a modal stack to track multiple modals - the class is only removed
 * when all modals are closed.
 * Also handles scroll position to prevent page jumping when modal opens/closes.
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * 
 * @example
 * const MyModal = ({ isOpen }) => {
 *   useModalOpen(isOpen);
 *   // ... rest of component
 * };
 */
export const useModalOpen = (isOpen) => {
  const modalIdRef = useRef(Symbol('modal'));
  
  useEffect(() => {
    const modalId = modalIdRef.current;
    
    if (isOpen) {
      modalStack.add(modalId);
    } else {
      modalStack.delete(modalId);
    }
    
    updateModalOpenClass();

    return () => {
      modalStack.delete(modalId);
      updateModalOpenClass();
    };
  }, [isOpen]);
};

/**
 * Scrolls the page to the top
 * Utility function that can be used in components
 * 
 * @example
 * scrollToTopOfPage();
 */
export const scrollToTopOfPage = scrollToTop;

/**
 * Gets the current scroll position
 * Utility function that can be used in components
 * 
 * @returns {number} Current scroll position in pixels
 * 
 * @example
 * const currentPosition = getCurrentScrollPosition();
 */
export const getCurrentScrollPosition = getScrollPosition;
